import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useRef, useState } from 'react';
import { SendbackAutoComplete } from '../components/sendbacks/sendback-autocomplete/sendback-autocomplete';
import { onEditAction } from './audit';
import { auditColumn, CollectionTable, Column, ColumnEvent, ColumnListener, deleteColumn, editColumn, FilterControl, iconStyle, shimColumn } from './collections';
import { Auto, Tags, Text } from './controls';
import { Person, ReturnItem } from './domain';
import { Expansion, ExpansionDetails, ExpansionSummary } from './expansion';
import { FormApi, formApi } from './form';
import { personName, truncateSpan, withTooltip } from './format';
import { ReturnApi } from './return-api';
import { darkWhite, mediumGray, orange, orignalGreen, origninalRed } from './theme';
import { UndoRedo } from './undo-redo';
import { isAuditReviewWorkflow, isReviewWorkflow } from './workflow-api';
import { formatDate, utcDateString } from './date-time';

export function ReturnForm(api: ReturnApi) {
  const { form, multiSendbackEnabled, list } = api;
  const { reason, sendToList, comment } = form.controls;
  const showProvider = true;
  return (
    <Grid
      container
      direction="row"
      xs={12}
      spacing={2}
      style={{
        width: '100%',
        flexShrink: 0,
        flexWrap: 'nowrap',
        paddingLeft: '12px',
        paddingTop: '6px',
        paddingBottom: '8px',
      }}
    >
      <Grid item xs={4}>
        {multiSendbackEnabled ? <SendbackAutoComplete control={reason} /> : <Auto {...reason} />}
      </Grid>
      {showProvider && (
        <Grid item xs={4}>
          {multiSendbackEnabled ? <Auto {...sendToList} /> : <Tags {...sendToList} />}
        </Grid>
      )}
      {!multiSendbackEnabled && (
        <Grid item xs={4}>
          <Text {...comment} />
        </Grid>
      )}
    </Grid>
  );
}

function sendbackIcon(e: ReturnItem) {
  const { newAction, isResolved } = e;

  const icon = isResolved ? 'check_circle' : newAction ? 'arrow_forward' : 'arrow_back';
  const color = isResolved || newAction ? orignalGreen : origninalRed;
  const tooltip = isResolved ? 'Resolved' : newAction ? newAction : 'To Provider';
  /*
  We need an indicator of how it will route.  As we know, different send-backs route differently.  
  The send-backs for USACS have a newAction which is either blank which indicates the send back will go to the provider,
  whereas, anything else means it may try to route elsewhere. 

  I think the indicator should be Red (or some color or shape) that indicates it will route to the provider (blank newAction) 
  and green (or some other color or shape) that indicates it has a newAction. 

  Hover text should be either the new action or “To Provider”. 
  Depending on our discussion with USACS, once a send back gets into DI, we may be able to forward it to provider, 
  but at least we’ll know how/why it got into DI.
  */
  const size = 32;
  return withTooltip(tooltip, <Icon style={{ color: color, minWidth: size, maxWidth: size, width: size }}>{icon}</Icon>);
}

function sendbackReason(e: ReturnItem) {
  let label = formatDate(utcDateString(e.createDate), 'MM/dd/yyyy') + ' - '
  const { category, subCategory } = e;
  if (category) {
    label += category;
  }
  if (subCategory) {
    label += ` - ${subCategory}`;
  }
  return label;
}

function resolutionComments(e: ReturnItem) {
  let comments = '';
  if (e?.resolutionInformation) {
    comments += e.resolutionInformation.resolutionComment || '';
  }
  return comments;
}
/*
We need a way to resolve the send back.  For example, if the send back is sent to DI and is requesting they upload a document, 
then they need to resolve the edit so that it stops routing and it won’t be able to be sent to TCL.  The SendBack object already has all the fields we need. 
When the user resolves the object, we should popup a modal to allow them to add a resolution comment.  The following fields get updated:
SendBack.isResolved = true
SendBack.resolution = ResolutionInformation object
*/
function resolveColumn(click: ColumnListener, activeCheck?: (a: ReturnItem) => boolean, label = '', id?: (e: ColumnEvent<ReturnItem>) => string): Column {
  const component = activeCheck
    ? (e: ColumnEvent<ReturnItem>) =>
        activeCheck(e.value) ? (
          <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
            <AssignmentTurnedInIcon />
          </IconButton>
        ) : (
          <IconButton id={id && id(e)} color="primary" style={{ pointerEvents: 'none' }}>
            <AssignmentTurnedInIcon htmlColor={mediumGray} />
          </IconButton>
        )
    : (e: ColumnEvent<ReturnItem>) => (
        <IconButton id={id && id(e)} color="primary" onClick={() => click(e)}>
          <AssignmentTurnedInIcon />
        </IconButton>
      );

  return {
    component,
    name: 'delete',
    label,
    click,
    align: 'center',
    style: iconStyle,
  };
}

export function ReturnCollection(api: ReturnApi) {
  const { list, context, multiSendbackEnabled } = api;
  const { auditSendBack } = context.config;

  const isReview = isAuditReviewWorkflow();
  const actions = isReview ? [auditColumn(onEditAction('Edit Send Back Audit Comments', () => list.actions.refresh(), false, auditSendBack))] : [];

  const columns: Column<ReturnItem>[] = multiSendbackEnabled
    ? [
        {
          label: '',
          component: (e) => sendbackIcon(e.value),
          style: iconStyle,
        },
        {
          name: 'returnText',
          label: 'Reason',
          component: (e) => truncateSpan(sendbackReason(e.value), '158px'),
        },
        {
          name: 'physician',
          label: 'Physician and Recipients',
          component: (e) => e.value.sendToList && truncateSpan(e.value.sendToList.map((v) => personName(v)).join(' - '), '9vw'),
        },
        {
          name: 'comment',
          label: 'Comments',
          component: (e) => truncateSpan(e.value.returnText || e.value.comment || '', '200px'),
        },
        {
          label: 'Resolution',
          component: (e) => truncateSpan(resolutionComments(e.value), '100px'),
        },
        {
          label: 'Cancel Code',
          //@ts-ignore cast
          component: (e) => truncateSpan( e.value.cancelCode || '', '80px'),
        },
        ...actions,
        resolveColumn(
          (e) => api.resolveItem(e.value),
          (a) => !a.isResolved,
          null,
          null
        ),
        editColumn(
          (e) => api.editItem(e.value),
          (a) =>  !a.isResolved
        ),
        deleteColumn(
          (e) => api.removeItem(e.value),
          (a) =>  !a.isResolved
        ),
        shimColumn(),
      ]
    : [
        {
          name: 'returnText',
          label: 'Reason',
          component: (e) => truncateSpan(multiSendbackEnabled ? e.value.description : e.value.returnText, '200px'),
        },
        {
          name: 'physician',
          label: 'Physician and Recipients',
          component: (e) => e.value.sendToList && truncateSpan(e.value.sendToList.map((v) => personName(v)).join(' - '), '36vw'),
        },
        {
          name: 'comment',
          label: 'Comments',
        },
        ...actions,
        editColumn((e) => api.editItem(e.value)),
        deleteColumn((e) => api.removeItem(e.value)),
        shimColumn(),
      ];

  const values = list.data((s) => s.values);
  const [expanded, setExpanded] = useState(values.length > 0);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0);
    }
  });

  const onClicked = () => {
    setExpanded(!expanded);
    setClicked(true);
  };

  return (
    <>
      <Expansion expanded={expanded}>
        <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="retrns_expend_more" />}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: 24,
              alignItems: 'center',
              flexWrap: 'nowrap',
            }}
          >
            <Typography variant="subtitle2">{`PROVIDER QUERIES ${values.length ? '- ' + values.length : ''}`}</Typography>
            <div style={{ display: 'flex', flex: 1, flexGrow: 1 }} />
            <div onClick={(e) => e.stopPropagation()}>
              <UndoRedo api={api.listChanges} htmlColor={orange} label="returns" />
            </div>
            <FilterControl api={list} />
          </div>
        </ExpansionSummary>
        <ExpansionDetails>
          <CollectionTable readonly={api.context.readonly} columns={columns} api={list} containerProps={{ elevation: 0 }} label={`PROVIDER QUERIES ${values.length ? '- ' + values.length : ''}`} />
        </ExpansionDetails>
      </Expansion>
      <ResolutionDialog api={api} />
      <SendbackEditDialog api={api} />
    </>
  );
}

type SendToProps = {
  list: Person[];
  selection?: Person[];
  label?: string;
};

type SendToForm = FormApi<{ selection: Person[] }>;

export function sendToForm(props: SendToProps): SendToForm {
  const { list, selection, label } = props;
  return formApi({
    values: {
      selection,
    },
    fields: {
      selection: {
        style: {
          minHeight: 68,
          maxHeight: 68,
        },
        label,
        list,
        listLabel: (v: Person) => personName(v),
      },
    },
  });
}

function SendTo(form: SendToForm) {
  const { selection } = form.controls;
  return <Tags {...selection} />;
}

type ReturnProps = {
  sendTo: SendToForm;
  returnReasonsList?: ReturnItem[];
  selected?: ReturnItem[];
};

function ReturnItemForm(item: ReturnItem) {
  const notes = useRef(
    formApi({
      values: {
        notes: null,
      },
      fields: {
        notes: {
          label: 'Notes',
          style: { width: '100%' },
          multiline: true,
          rows: 2,
          clearIcon: false,
        },
      },
    })
  ).current;
  return (
    <>
      <Card>
        <CardContent>
          <Typography color="primary" style={{ fontWeight: 'bold' }}>
            {item.description}
          </Typography>
          <Text {...notes.controls.notes} />
        </CardContent>
      </Card>
      <br />
    </>
  );
}

export function Return(props: ReturnProps) {
  const { sendTo, returnReasonsList } = props;

  const [selected, setSelected] = useState(props.selected || []);

  const toggleSelected = (v: ReturnItem) => () => {
    v.selected = !v.selected;
    setSelected(returnReasonsList.filter((v) => v.selected));
  };

  const maxHeight: any = { width: 300, maxHeight: 500, overflowY: 'scroll' };
  return (
    <Grid container direction="column" style={{ paddingTop: 0, width: '100%' }}>
      <Grid item style={{ paddingBottom: 24, paddingTop: 0 }}>
        <SendTo {...sendTo} />
      </Grid>
      <Grid item container direction="row" spacing={2}>
        <Grid item container xs={6} direction="column" style={{ width: '100%' }}>
          <List dense style={maxHeight}>
            {returnReasonsList.map((v: ReturnItem, i) => {
              const toggle = toggleSelected(v);
              return (
                <ListItem onClick={toggle} key={i} button style={v.selected ? { backgroundColor: orange, color: 'white' } : undefined}>
                  <ListItemText primary={v.description} />
                </ListItem>
              );
            })}
          </List>
        </Grid>
        <Grid item container xs={6} direction="column" style={{ width: '100%', backgroundColor: darkWhite }}>
          <Grid item>
            <List dense style={maxHeight}>
              {selected.map((v: ReturnItem, i) => (
                <ReturnItemForm key={i} {...v} />
              ))}
            </List>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
type ResolutionDialogProps = {
  api: ReturnApi;
};
const useStyles = makeStyles(() => ({
  paper: { minWidth: 482 },
}));

function ResolutionDialog({ api }: ResolutionDialogProps) {
  const { data, resolutionForm } = api;
  const item = data((s) => s.resolveItem);

  const showCancelCodes = data.getState().showCancelCodes;

  const onCancel = () => {
    resolutionForm.setValues({ comments: '' });
    data.setState({ resolveItem: null });
  };

  const onSubmit = () => {
    resolutionForm.submit();
  };
  useEffect(() => {
    if (item) {
      resolutionForm.setValues({ comments: item.resolutionInformation?.resolutionComment, cancelCode: item.cancelCode });
    } else {
      resolutionForm.setValues({ comments: '' });
    }
  });
  const classes = useStyles();
  return item ? (
    <Dialog open={!!item} classes={{ paper: classes.paper }}>
      <DialogTitle>Resolve Send Back</DialogTitle>

      <DialogContent style={{ zIndex: 1501, overflow: 'hidden' }}>

        {showCancelCodes && (
          <Grid item style={{ ...{ padding: 0 } }}>
            <Auto {...resolutionForm.controls.cancelCode} />
          </Grid>
        )}

        <Grid item style={{ ...{ padding: 0 } }}>
          <Text {...resolutionForm.controls.comments} />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
}



function SendbackEditDialog({ api }: ResolutionDialogProps) {
  const { data, sendbackForm } = api;
  const item = data((s) => s.editItem);

  const onCancel = () => {
    sendbackForm.setValues({ comments: '' });
    data.setState({ editItem: null });
  };

  const onSubmit = () => {
    sendbackForm.submit();
  };
  useEffect(() => {
    if(!sendbackForm){
      return;
    }
    if (item) {
      sendbackForm.setValues({ comments: item.comment});
    } else {
      sendbackForm.setValues({ comments: '' });
    }
  });
  const classes = useStyles();
  return item ? (
    <Dialog open={!!item} classes={{ paper: classes.paper }}>
      <DialogTitle>Edit Send Back Comments</DialogTitle>

      <DialogContent style={{ zIndex: 1501, overflow: 'hidden' }}>

        <Grid item style={{ ...{ padding: 0 } }}>
          <Text {...sendbackForm.controls.comments} />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
}